import { Position } from "@xyflow/react";

export const HANDLE_CAP = 48;
const SIDE_ORDER = ["left", "right", "top", "bottom"];

export function portSide(p) {
  const s = (p?.side || "").toLowerCase();
  if (s === "left" || s === "west") return Position.Left;
  if (s === "right" || s === "east") return Position.Right;
  if (s === "top" || s === "north") return Position.Top;
  if (s === "bottom" || s === "south") return Position.Bottom;
  const dir = (p?.direction || "").toLowerCase();
  if (dir === "in" || dir === "input") return Position.Left;
  if (dir === "out" || dir === "output") return Position.Right;
  if ((p?.kind || "") === "power") return Position.Bottom;
  return Position.Right;
}

function sideName(pos) {
  if (pos === Position.Left) return "left";
  if (pos === Position.Top) return "top";
  if (pos === Position.Bottom) return "bottom";
  return "right";
}

/** First 48 port names after side order, then name. Sole owner of the cap. */
export function emittedHandleIds(ports) {
  const buckets = { left: [], right: [], top: [], bottom: [] };
  for (const p of ports || []) {
    if (!p?.name) continue;
    buckets[sideName(portSide(p))].push(p.name);
  }
  const ordered = [];
  for (const side of SIDE_ORDER) {
    const names = [...new Set(buckets[side])].sort((a, b) => a.localeCompare(b));
    ordered.push(...names);
  }
  return new Set(ordered.slice(0, HANDLE_CAP));
}

/** Dual-type Handle defs. Same id on source + target (XYFlow looks up by type). */
export function portHandleDefs(ports) {
  const allowed = emittedHandleIds(ports);
  const bySide = { left: [], right: [], top: [], bottom: [] };
  for (const p of ports || []) {
    if (!p?.name || !allowed.has(p.name)) continue;
    bySide[sideName(portSide(p))].push(p);
  }
  const out = [];
  for (const [side, list] of Object.entries(bySide)) {
    const n = list.length;
    list.forEach((p, i) => {
      const t = (i + 1) / (n + 1);
      // Keep handles out of the title band (≈52px) and Enter row (≈24px).
      const style =
        side === "left" || side === "right"
          ? { top: `calc(52px + ${t} * (100% - 76px))` }
          : { left: `${t * 100}%` };
      const position = {
        left: Position.Left,
        right: Position.Right,
        top: Position.Top,
        bottom: Position.Bottom,
      }[side];
      out.push({ id: p.name, type: "target", position, style, kind: p.kind, name: p.name });
      out.push({ id: p.name, type: "source", position, style, kind: p.kind, name: p.name });
    });
  }
  return out;
}

export function portOf(endpoint) {
  const s = String(endpoint || "");
  const i = s.lastIndexOf(".");
  return i > 0 ? s.slice(i + 1) : null;
}

export function handleSet(mod) {
  return emittedHandleIds(mod?.ports);
}

/** Emit an edge only when both handle ids are in the emitted set. */
export function edgeHandles(l, fromMod, toMod, byName) {
  const sh = portOf(l.from);
  const th = portOf(l.to);
  const srcOk = sh && handleSet(byName[fromMod]).has(sh);
  const tgtOk = th && handleSet(byName[toMod]).has(th);
  if (!srcOk || !tgtOk) return null;
  return { sourceHandle: sh, targetHandle: th };
}

const PARK_GAP = 48;

/** Compact page-port rail. Ignores compiler container w/h. */
export function parkFocusSheet(sheetMod, siblingMods) {
  const ports = sheetMod?.ports || [];
  const left = ports.filter((p) => portSide(p) === Position.Left).length;
  const right = ports.filter((p) => portSide(p) === Position.Right).length;
  const vert = Math.max(0, ports.length - left - right);
  const h = Math.max(76 + 14 * Math.max(left, right, 1), 56 + 14 * Math.max(vert, 1));
  const longest = ports.reduce((n, p) => Math.max(n, String(p.name || "").length), 4);
  const w = Math.max(220, 72 + 8 * longest);
  let minX = Infinity;
  let minY = Infinity;
  for (const c of siblingMods || []) {
    if (!c || c.name === sheetMod?.name) continue;
    minX = Math.min(minX, c.x ?? 0);
    minY = Math.min(minY, c.y ?? 0);
  }
  if (!Number.isFinite(minX)) {
    return { x: sheetMod?.x ?? 0, y: sheetMod?.y ?? 0, w, h };
  }
  return { x: minX - PARK_GAP - w, y: minY, w, h };
}


